/**
 * OAuth Success Callback Page
 *
 * Handles successful OAuth authentication from providers (Google, GitHub, Microsoft)
 * Extracts token from URL fragment (#) and stores it, then redirects to dashboard.
 * Using fragment instead of query string prevents token leakage via Referer headers,
 * browser history, and server logs.
 */
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';

export const OAuthSuccess: React.FC = () => {
  const navigate = useNavigate();
  const authStore = useAuthStore();

  useEffect(() => {
    const handleOAuthCallback = async () => {
      // Parse token from URL fragment (hash) — fragments are never sent to servers
      const hash = window.location.hash.substring(1); // strip leading '#'
      const fragmentParams = new URLSearchParams(hash);
      const token = fragmentParams.get('token');
      const provider = fragmentParams.get('provider');

      if (!token) {
        console.error('❌ No token in OAuth callback');
        const errorMsg = 'OAuth authentication failed - no token received. Please try again.';
        navigate(`/auth/login?error=${encodeURIComponent(errorMsg)}`, { replace: true });
        return;
      }

      try {
        console.log(`✅ OAuth login successful via ${provider}`);

        // IMPORTANT: Clear any stale localStorage data first
        // OAuth users might have old user_type data that doesn't match their current token
        localStorage.removeItem('user_type');
        localStorage.removeItem('bgai-auth-storage');
        localStorage.removeItem('token');

        console.log('🧹 Cleared stale localStorage data for fresh OAuth login');

        // Store token in localStorage first (required for API calls)
        localStorage.setItem('token', token);

        // Important: Set token in axios headers for immediate API calls
        const { api } = await import('../../services/api');
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        // CRITICAL: Set token in Zustand store BEFORE calling refreshUser()
        // refreshUser() needs get().token to be available
        useAuthStore.setState({
          token,
          isLoading: true,
          error: null
        });

        // Now fetch user data using the token
        console.log('🔍 Fetching user data with OAuth token...');

        const userData = await authStore.refreshUser();

        console.log(`✅ User data loaded:`, userData);

        if (!userData) {
          throw new Error('Failed to fetch user data after OAuth login');
        }

        // NOW store everything properly in the same format as regular login
        const userType = userData.user_type || userData.role || 'company_owner';

        // Store in bgai-auth-storage format (same as regular login)
        const authData = {
          state: {
            user: userData,
            token: token,
            isAuthenticated: true
          }
        };

        localStorage.setItem('bgai-auth-storage', JSON.stringify(authData));
        localStorage.setItem('user_type', userType);
        localStorage.setItem('token', token);

        console.log('💾 Stored auth data in localStorage:', { userType, userId: userData.id });

        // Set complete auth state in Zustand store
        useAuthStore.setState({
          token,
          user: userData,
          isAuthenticated: true,
          isLoading: false,
          error: null
        });

        console.log(`✅ Auth state fully configured, preparing to redirect`);

        // Wait for auth state to fully propagate before redirecting
        // This prevents race conditions where protected routes see stale auth state
        await new Promise(resolve => setTimeout(resolve, 200));

        // Redirect to appropriate dashboard based on user type
        const redirectPath = userType === 'agent' ? '/agent/dashboard' : '/dashboard';

        console.log(`✅ Redirecting ${userType} to ${redirectPath}`);
        navigate(redirectPath, { replace: true });

      } catch (error) {
        console.error('❌ OAuth callback error:', error);

        // Clear invalid token
        localStorage.removeItem('token');

        // Clear auth state
        useAuthStore.setState({
          token: null,
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: null
        });

        // Extract meaningful error message
        let errorMessage = 'oauth_failed';
        if (error instanceof Error) {
          errorMessage = error.message;
        } else if (typeof error === 'object' && error !== null) {
          errorMessage = (error as any).message || (error as any).detail || 'oauth_failed';
        }

        // Redirect to login page with detailed error
        // The error will be displayed in the dismissible alert on LoginPage
        navigate(`/auth/login?error=${encodeURIComponent(errorMessage)}`, { replace: true });
      }
    };

    handleOAuthCallback();
  }, [navigate, authStore]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <h2 className="text-xl font-semibold text-gray-900">Logging you in...</h2>
        <p className="text-gray-600 mt-2">Please wait while we complete your authentication.</p>
      </div>
    </div>
  );
};

export default OAuthSuccess;
